import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { HardHat, WifiSlash } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { loadSession } from "@/lib/sessionCache";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import LoginTutorial from "@/components/LoginTutorial";
import SecurityInfo from "@/components/SecurityInfo";
import IntroSplash, { shouldShowIntro } from "@/components/IntroSplash";

export default function Login() {
  const { t, lang, switchLang } = useLang();
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [cached, setCached] = useState(null);
  const [showIntro, setShowIntro] = useState(() => shouldShowIntro());

  // Detect cached offline session so we can offer a "Continue offline" CTA
  // when the device has no connectivity. This unblocks miners who took a
  // shift underground without logging out first.
  useEffect(() => {
    loadSession().then(setCached).catch(() => setCached(null));
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      // Force temporary passwords (bulk-from-HR) to be rotated on first login.
      if (data.must_change_password) {
        navigate("/change-password", { replace: true });
        return;
      }
      // Redirigimos según los permisos efectivos: si el usuario no tiene
      // dashboard.view (típicamente operador/mecánico/almacenista), lo
      // llevamos directo a su espacio personal /me.
      const perms = data.permissions || [];
      const dest = perms.includes("dashboard.view") ? "/" : "/me";
      navigate(dest);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const resumeOffline = () => {
    if (!cached?.user) return;
    setUser({ ...cached.user, _offline: true, _offline_expires_at: cached.expires_at });
    const perms = cached.user.permissions || [];
    const dest = perms.includes("dashboard.view") ? "/" : "/me";
    navigate(dest);
    toast.info(t("resumedOfflineToast"));
  };

  const showOfflineCta = !online && !!cached;
  const cachedExpiryTxt = cached?.expires_at
    ? new Date(cached.expires_at).toLocaleDateString(lang === "es" ? "es-MX" : "en-US", {
        day: "2-digit", month: "short", year: "numeric",
      })
    : "";

  return (
    <>
      {showIntro && <IntroSplash onDismiss={() => setShowIntro(false)} />}
    <div
      className="min-h-screen flex items-center justify-center bg-slate-900 relative"
      style={{
        backgroundImage:
          "linear-gradient(rgba(2, 6, 23, 0.55), rgba(2, 6, 23, 0.75)), url(/img/login-underground-bg.png)",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
      data-testid="login-hero"
    >
      <div className="absolute top-6 right-6 flex gap-1 bg-white/90 backdrop-blur rounded-md p-1 border border-slate-200">
        <button data-testid="login-lang-es" onClick={() => switchLang("es")} className={`text-xs font-bold px-2 py-1 rounded ${lang === "es" ? "bg-blue-900 text-white" : "text-slate-500"}`}>ES</button>
        <button data-testid="login-lang-en" onClick={() => switchLang("en")} className={`text-xs font-bold px-2 py-1 rounded ${lang === "en" ? "bg-blue-900 text-white" : "text-slate-500"}`}>EN</button>
      </div>
      {/* MAFRISSA branding — esquina superior izquierda del hero */}
      <div className="absolute top-6 left-6 flex items-center gap-3 bg-white/95 backdrop-blur rounded-lg px-3 py-2 border border-slate-200 shadow-lg"
           data-testid="login-mafrissa-badge">
        <img src="/img/mafrissa-logo.png" alt="MAFRISSA — Transportes y Maquinaria S.A. de C.V."
             className="h-11 w-11 object-contain" />
        <div className="leading-tight">
          <p className="font-heading font-black text-sm tracking-tight text-red-700">MAFRISSA</p>
          <p className="text-[9px] uppercase tracking-widest font-bold text-slate-500">Transportes y Maquinaria</p>
        </div>
      </div>
      <div className="w-full max-w-md bg-white/95 backdrop-blur border border-slate-200 rounded-xl shadow-[0_16px_60px_rgba(0,0,0,0.4)] p-10">
        <div className="flex items-center gap-3 mb-2">
          <div className="h-10 w-10 rounded-md bg-blue-900 flex items-center justify-center">
            <HardHat size={22} weight="fill" className="text-amber-400" />
          </div>
          <div className="leading-tight">
            <h1 className="font-heading font-black text-xl tracking-tight text-slate-900">
              UG <span className="text-blue-900">Managment</span>
            </h1>
            <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">by OOMINING</p>
          </div>
        </div>
        <p className="text-sm text-slate-500 mb-8">{t("loginSubtitle")}</p>

        {showOfflineCta && (
          <div data-testid="login-offline-resume" className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4">
            <div className="flex items-center gap-2 text-amber-900 font-black text-xs uppercase tracking-widest">
              <WifiSlash size={14} weight="bold" />
              {t("offlineDetected")}
            </div>
            <p className="text-xs text-amber-900/80 mt-2">
              {t("cachedSessionAvailable", { name: cached?.user?.name || cached?.user?.email || "" })}
            </p>
            <p className="text-[10px] text-amber-800/70 mt-1">
              {t("cachedSessionExpires")}: <b>{cachedExpiryTxt}</b>
            </p>
            <Button
              data-testid="login-resume-offline-btn"
              onClick={resumeOffline}
              className="w-full mt-3 bg-amber-500 hover:bg-amber-600 text-white font-bold"
            >
              {t("continueOffline")}
            </Button>
          </div>
        )}

        <form onSubmit={submit} className="space-y-5">
          <div className="space-y-2">
            <Label className="overline-label">{t("email")}</Label>
            <Input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@minera.com" />
          </div>
          <div className="space-y-2">
            <Label className="overline-label">{t("password")}</Label>
            <Input data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
          </div>
          <Button data-testid="login-submit-button" type="submit" disabled={loading} className="w-full bg-blue-900 hover:bg-blue-800 font-semibold">
            {loading ? "..." : t("signIn")}
          </Button>
        </form>
        <LoginTutorial />
        <SecurityInfo />
        <div className="mt-4 text-center">
          <a data-testid="login-forgot-link" href="/forgot-password"
              className="text-xs text-slate-500 hover:text-blue-900 transition-colors">
            {t("forgotPasswordLink")}
          </a>
        </div>
        <div className="mt-6 pt-5 border-t border-slate-200 text-center">
          <a
            data-testid="login-verify-link"
            href="/verify"
            className="inline-flex items-center gap-2 text-xs font-semibold text-blue-900 hover:text-amber-500 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></svg>
            {t("verifyPdfLink")}
          </a>
          <p className="text-[10px] text-slate-500 mt-1">{t("verifyPdfHint")}</p>
        </div>
      </div>
    </div>
    </>
  );
}
